import dayjs from 'dayjs'
import queryString from 'query-string'

import { useRoute } from 'vue-router'
import { Base64 } from 'js-base64'
import { JSEncrypt } from 'jsencrypt'
import { $t } from '@/i18n'
import { useTitle } from '@vueuse/core'

export { throttle, debounce, cloneDeep, merge } from 'lodash-es'

/**
 * @desc  格式化时间
 * @param {(Object|string|number)} time
 * @param {string} format
 * @returns {string | null}
 */
export function formatDateTime(time = undefined, format = 'YYYY-MM-DD HH:mm:ss') {
  return dayjs(time).format(format)
}

export function formatDate(date = undefined, format = 'YYYY-MM-DD') {
  return formatDateTime(date, format)
}

export function isExternal(path: string) {
  return /^(https?:|mailto:|tel:)/.test(path)
}

/**
 * 根据当前域名获取env
 * @param host
 * @returns
 */
export function getEnv(host = location.host) {
  const isVip = host.includes('.vip')
  if (isVip) {
    host = host.slice(host.lastIndexOf('.vip'))
  }
  const envMap = {
    dev: 'dev',
    sit: 'sit',
    petest: 'petest',
    uat: 'uat',
    demo: 'demo'
  }

  if (import.meta.env.MODE === 'development') {
    const transEnv = (localStorage.getItem('_baseURL') ?? '/dev').replace('/', '')
    return envMap[transEnv]
  }

  for (const i of Object.keys(envMap)) {
    if (host.includes(i)) {
      return envMap[i]
    }
  }
  return undefined
}

// 获取 静态图片资源
export function getAssetsImg(url: string) {
  const path = `/src/assets/images/${url}` //拼接文件路径
  const modules: any = import.meta.glob('@/assets/images/*')
  return modules[path]()
}

// 拿到请求参数
export function getUrlQuery(value: string) {
  return useRoute()?.query[value]
}

// 拿到请求参数
export function getParams(paras: string, url = window.location.href) {
  // var url = window.location.href
  const paraString = url.substring(url.indexOf('?') + 1, url.length).split('&')
  const paraObj = {}
  for (let i = 0; i < paraString.length; i++) {
    const j = paraString[i]
    paraObj[j.substring(0, j.indexOf('=')).toLowerCase()] = j.substring(
      j.indexOf('=') + 1,
      j.length
    )
  }
  const returnValue = paraObj[paras.toLowerCase()]
  if (typeof returnValue === 'undefined') {
    return ''
  } else {
    return decodeURIComponent(returnValue)
  }
}

// 验证手机号
export function validatePhoneNumber(phoneNumber: any) {
  // 正则
  const phoneRegex = /^[1-9]\d{5,14}$/
  // 返回值
  const params = {
    flag: false,
    type: 0, // 0 手机号为空, 1 手机号长度大于11位数, 2 校验正则: 错误, 3校验正则 正确
    msg: ''
  }

  if (String(phoneNumber).length === 0 || phoneNumber === null || phoneNumber === undefined) {
    params.flag = false
    params.type = 0
    params.msg = `${$t('login_enter_phone')}`
  } else if (String(phoneNumber).length > 15) {
    params.flag = false
    params.type = 1
    params.msg = `${$t('login_phone_format_error')}`
  } else if (!phoneRegex.test(String(phoneNumber))) {
    params.flag = false
    params.type = 2
    params.msg = `${$t('login_phone_format_error')}`
  } else {
    params.flag = true
    params.type = 3
    params.msg = '校验通过'
  }
  return params
}

// 替换手机号
export function phoneNumber_replace(phoneNumber: string) {
  return phoneNumber.replace(/\D/g, '').slice(0, 15)
}

// 验证码: 校验和替换
export function verificationCode_verify_replace(type: 'verify' | 'replace', value: string) {
  if (type === 'replace') {
    return value.replace(/\D/g, '').slice(0, 6)
  } else if (type === 'verify') {
    const regex = /\b\d{6}\b/g
    return regex.test(value)
  }
}

// 密码校验
export function password_verify_replace(type: 'verify' | 'replace', value: string) {
  if (type === 'replace') {
    const _str = value.replace(/\s*/g, '').replace(/[\u4e00-\u9fa5]/g, '')
    // 去除空格
    return _str
  }

  if (type === 'verify') {
    const _res = {
      flag: false,
      msg: ''
    }

    if (!value) {
      _res.flag = false
      _res.msg = `${$t('login_enter_password')}`
    } else if (
      !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d!@#$%^&*()_+\-=.<>?~,]{8,20}$/.test(value)
    ) {
      _res.flag = false
      _res.msg = `${$t('login_password_rules')}`
    } else {
      _res.flag = true
      _res.msg = ''
    }
    return _res
  }
}
// Base64 加密解密
export function base64Code(type: 'encode' | 'decode', value: any) {
  if (type === 'encode') {
    return Base64.encode(String(value))
  } else if (type === 'decode') {
    return Base64.decode(String(value))
  }
}

// 加密
export function encryptedData(msg: string) {
  const PUBLIC_KEY: string = import.meta.env.VITE_PUBLIC_KEY
  const encrypt = new JSEncrypt()
  encrypt.setPublicKey(PUBLIC_KEY)
  return encrypt.encrypt(msg)
}

/**
 * @param {string} str
 * @returns {Boolean}
 */
export function isPrimitive(val: any) {
  return val === null || (typeof val !== 'object' && typeof val !== 'function')
}

/**
 * 解析当前页面哈希（#后面的部分）中的查询参数。
 *
 * @returns {Object} 返回一个对象，包含解析后的查询参数。
 */
export function getQueryParamsInHash() {
  const hash = location.hash
  const index = hash.indexOf('?')
  const hasQueryString = hash.substring(index + 1)
  const queryParams = queryString.parse(hasQueryString)
  return queryParams
}

/**
 * 复制给定的字符串到剪贴板
 * @param val 需要被复制的字符串
 */
export function copy(val: string) {
  const input = document.createElement('input')
  document.body.appendChild(input)
  input.readOnly = true
  input.setAttribute('value', val)

  input.select()
  document.execCommand('Copy')
  document.body.removeChild(input)
  window.$message.success('复制成功')
}

/**
 * 获取资源的URL
 * @param image 相对路径的图片名称
 * @returns 返回图片的完整URL
 */
export function getAssetURL(image: string) {
  // 参数一: 相对路径
  // 参数二: 当前路径的URL
  return new URL(`../assets/images/${image}`, import.meta.url).href
}

export function isIOS() {
  const ua = navigator.userAgent
  const isIOS = !!ua.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/) // 判断是否是 iOS
  return isIOS
}

export function isAndroid() {
  const ua = navigator.userAgent
  const isAndroid = ua.indexOf('Android') > -1 || ua.indexOf('Adr') > -1 // 判断是否是 android终端
  return isAndroid
}

export function isHarmonyOS() {
  const ua = navigator.userAgent
  const isHarmonyOS = ua.indexOf('HarmonyOS') > -1 // 判断是否是鸿蒙系统
  return isHarmonyOS
}

export function isPad() {
  const userAgent = navigator.userAgent
  return (
    /iPad|Tablet|Android(?!.*Mobile)/i.test(userAgent) ||
    (/(iPhone|iPod|iPad)/i.test(userAgent) && !/Mobile/i.test(userAgent)) // 判断是否是 pad终端
  )
}

export function getQueryParam(param) {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get(param)
}
export function isMobile() {
  if (isPad()) {
    return false
  }
  return isAndroid() || isIOS() || isHarmonyOS()
}

export function getpcode() {
  let pcode = ''
  if (getParams('pcode')) {
    pcode = getParams('pcode')
  } else {
    if (getParams('redirectUrl')) {
      pcode = getParams('pcode', decodeURIComponent(getParams('redirectUrl')))
    }
  }
  return pcode || ''
}

export function getClientId() {
  let clientId = ''
  if (getParams('clientId')) {
    clientId = getParams('clientId')
  } else {
    if (getParams('redirectUrl')) {
      clientId = getParams('clientId', decodeURIComponent(getParams('redirectUrl')))
    }
  }
  return clientId || ''
}

export function fromPortal() {
  const clientId = getClientId()
  return clientId === 'portal'
}

export function setTitleByRoute(route: any) {
  const defaultTitle = location.href.split('://')[1]
  let pageTitle = route.meta?.title
  if (route.meta && typeof route.meta.titleI18nKey === 'string') {
    pageTitle = $t(route.meta.titleI18nKey, defaultTitle)
  }
  if (pageTitle) {
    useTitle().value = `${pageTitle}`
  } else {
    useTitle().value = $t('key_KuaiLu', defaultTitle) as string
  }
}

export function replaceDomain(url: string) {
  const mapping = {
    'dev-auth': 'www-dev',
    'sit-auth': 'www-sit',
    'petest-auth': 'www-petest',
    'uat-auth': 'www-uat',
    'demo-auth': 'www-demo',
    auth: 'www'
  }

  const regex = new RegExp(Object.keys(mapping).join('|'), 'g')

  return url?.replace(regex, (match) => mapping[match])
}

export function isVip() {
  return location.host.includes('.authvip')
}

function getCachedRedirectUrl(): string {
  try {
    const raw = localStorage.getItem('cache')
    if (!raw) return ''
    const parsed = JSON.parse(raw) as { redirectUrl?: string }
    return String(parsed?.redirectUrl ?? '')
  } catch {
    return ''
  }
}

/** 快鹭会议官网友链路（认证域或 redirectUrl 来自 kuailumeeting.com） */
export function isMeetingDomain() {
  const hostname = window.location.hostname
  if (hostname.includes('kuailumeeting.com')) {
    return true
  }
  const redirectUrl = getParams('redirectUrl') || getCachedRedirectUrl()
  if (!redirectUrl) {
    return false
  }
  try {
    return decodeURIComponent(redirectUrl).includes('kuailumeeting.com')
  } catch {
    return redirectUrl.includes('kuailumeeting.com')
  }
}

/** 账户设置页 header logo（public 下静态资源文件名） */
export function getSettingLogoFileName(locale: string): string {
  const base = isMeetingDomain() ? 'metting-logo' : 'logo'
  return `${base}-${locale}.png`
}
